// CLP outputs — computed tables + recharts visualisations + PDF export.
import { useCallback, useEffect, useState } from "react";
import {
    ResponsiveContainer, ComposedChart, BarChart, Bar, Line, XAxis, YAxis,
    CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Download, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, filenameFromResponse } from "@/lib/api";
import { fmtNum } from "@/lib/clp";

const C = { brand: "#C75A1B", teal: "#2A6F6B", amber: "#C68A1C", ink: "#1F1F1F", muted: "#6F6A64" };

function Card({ title, subtitle, children, testid }) {
    return (
        <div className="bg-white border rounded-2xl p-4" style={{ borderColor: "#E5E0DA" }} data-testid={testid}>
            <h3 className="font-semibold text-[#1F1F1F]">{title}</h3>
            {subtitle && <p className="text-xs text-[#6F6A64] mt-0.5">{subtitle}</p>}
            <div className="mt-3">{children}</div>
        </div>
    );
}

function Tbl({ head, rows, aligns = {} }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
                <thead>
                    <tr style={{ background: C.brand }}>
                        {head.map((h, i) => (
                            <th key={i} className="text-left text-white font-semibold px-2.5 py-1.5 whitespace-nowrap"
                                style={{ textAlign: aligns[i] || "left" }}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, ri) => (
                        <tr key={ri} className={r._peak ? "font-semibold" : ""}
                            style={{ background: r._peak ? "#FBEFE5" : ri % 2 ? "#F6F4F0" : "#fff" }}>
                            {r.cells.map((c, ci) => (
                                <td key={ci} className="px-2.5 py-1.5 border-b" style={{ borderColor: "#EDE9E3", textAlign: aligns[ci] || "left" }}>{c}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function ClpOutputs({ clpId, version }) {
    const [outputs, setOutputs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/clp/${clpId}/outputs`);
            setOutputs(data.outputs);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to compute outputs");
        } finally { setLoading(false); }
    }, [clpId, version]);

    useEffect(() => { load(); }, [load]);

    const exportPdf = async () => {
        setExporting(true);
        try {
            const res = await api.get(`/clp/${clpId}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = filenameFromResponse(res, `CLP-${clpId}.pdf`); a.click();
            URL.revokeObjectURL(url);
            toast.success("PDF downloaded");
        } catch (e) {
            toast.error("PDF export failed");
        } finally { setExporting(false); }
    };

    if (loading) return <div className="grid place-items-center py-20 text-[#6F6A64]"><Loader2 className="w-6 h-6 animate-spin" /></div>;
    if (!outputs) return null;

    const o = outputs;
    const v = o.validation || { errors: [], warnings: [] };
    const peak = o.peak_month_label || "—";

    return (
        <div className="space-y-4" data-testid="clp-outputs">
            <div className="flex items-center justify-between">
                <div className="flex gap-2 flex-wrap">
                    <Metric label="Peak month" value={peak} />
                    <Metric label="Peak monthly trips" value={fmtNum(o.peak_month_total)} />
                    <Metric label="Peak daily trips" value={fmtNum(o.peak_daily)} />
                    <Metric label="Programme length" value={`${(o.axis || []).length} mo`} />
                </div>
                <button onClick={exportPdf} disabled={exporting} data-testid="clp-export-pdf"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                    style={{ background: C.brand }}>
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export PDF
                </button>
            </div>

            {(v.errors.length > 0 || v.warnings.length > 0) && (
                <div className="rounded-xl p-3 text-sm" data-testid="clp-validation"
                    style={{ background: v.errors.length ? "#FBE8E5" : "#FCF1DC", color: v.errors.length ? "#B33A2E" : "#8A6D1C" }}>
                    <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="w-4 h-4" />
                        {v.errors.length ? "Please fix before this CLP is valid" : "Warnings"}</div>
                    <ul className="mt-1.5 list-disc ml-5 space-y-0.5">
                        {[...v.errors, ...v.warnings].map((e, i) => <li key={i}>{e.message}</li>)}
                    </ul>
                </div>
            )}

            <Card title="Construction programme" subtitle="Gantt-style overview of each stage" testid="clp-gantt">
                <Gantt programme={o.charts.programme} axis={o.axis} axisLabels={o.axis_labels} />
            </Card>

            <Card title="Total vehicles through the programme" subtitle="Monthly vehicle trips (bars) and average daily total (line)" testid="clp-monthly-chart">
                <ResponsiveContainer width="100%" height={280}>
                    <ComposedChart data={o.charts.monthly} margin={{ top: 10, right: 16, bottom: 30, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EDE9E3" />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.muted }} angle={-35} textAnchor="end" height={50} />
                        <YAxis yAxisId="l" tick={{ fontSize: 11, fill: C.muted }} />
                        <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: C.muted }} />
                        <Tooltip />
                        <Legend />
                        <Bar yAxisId="l" dataKey="total" name="Vehicles / month" fill={C.brand} radius={[3, 3, 0, 0]} />
                        <Line yAxisId="r" dataKey="avg_daily" name="Avg / day" stroke={C.teal} strokeWidth={2} dot={false} />
                    </ComposedChart>
                </ResponsiveContainer>
            </Card>

            <div className="grid lg:grid-cols-2 gap-4">
                <Card title="Vehicles in peak phase (excluding overlaps)" testid="clp-trips-excl">
                    <Tbl head={["Stage", "Period", "Monthly", "Daily"]} aligns={{ 2: "right", 3: "right" }}
                        rows={o.trips_excl.map((t) => ({ _peak: t.is_peak, cells: [t.name, t.period, fmtNum(t.monthly), fmtNum(t.daily)] }))} />
                </Card>
                <Card title="Vehicles in peak phase (incl. overlap of subsequent phases)" testid="clp-trips-incl">
                    <Tbl head={["Stage", "Period", "Monthly", "Daily"]} aligns={{ 2: "right", 3: "right" }}
                        rows={o.trips_incl.map((t) => ({ cells: [t.name, t.period, fmtNum(t.monthly), fmtNum(t.daily)] }))} />
                </Card>
            </div>

            <Card title={`Vehicles by type during peak month (${peak})`} subtitle="Split of each stage's peak-month vehicles by weight class" testid="clp-types">
                <div className="grid lg:grid-cols-2 gap-4 items-start">
                    <Tbl head={["Stage", "≤3.5t", "3.5–7.5t", "≥7.5t", "Total"]} aligns={{ 1: "right", 2: "right", 3: "right", 4: "right" }}
                        rows={[
                            ...o.types_peak_month.rows.map((r) => ({ cells: [r.name, fmtNum(r.le35), fmtNum(r.mid), fmtNum(r.ge75), fmtNum(r.total)] })),
                            { _peak: true, cells: ["Total", fmtNum(o.types_peak_month.total.le35), fmtNum(o.types_peak_month.total.mid), fmtNum(o.types_peak_month.total.ge75), fmtNum(o.types_peak_month.total.total)] },
                        ]} />
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={o.charts.types} margin={{ top: 10, right: 10, bottom: 30, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#EDE9E3" />
                            <XAxis dataKey="name" tick={{ fontSize: 9, fill: C.muted }} angle={-30} textAnchor="end" height={60} interval={0} />
                            <YAxis tick={{ fontSize: 11, fill: C.muted }} />
                            <Tooltip /><Legend />
                            <Bar dataKey="le35" name="≤3.5t" stackId="a" fill={C.brand} />
                            <Bar dataKey="mid" name="3.5–7.5t" stackId="a" fill={C.teal} />
                            <Bar dataKey="ge75" name="≥7.5t" stackId="a" fill={C.amber} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </Card>

            <Card title={`Hourly distribution of vehicles — peak month (${peak})`} subtitle="Vehicles arriving per hour and estimated minutes between arrivals" testid="clp-hourly">
                <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={o.charts.hourly} margin={{ top: 10, right: 16, bottom: 10, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EDE9E3" />
                        <XAxis dataKey="hour" tick={{ fontSize: 10, fill: C.muted }} interval={1} />
                        <YAxis tick={{ fontSize: 11, fill: C.muted }} />
                        <Tooltip />
                        <Bar dataKey="vehicles" name="Vehicles / hour" fill={C.teal} radius={[3, 3, 0, 0]} />
                    </ComposedChart>
                </ResponsiveContainer>
            </Card>
        </div>
    );
}

function Metric({ label, value }) {
    return (
        <div className="bg-white border rounded-xl px-3.5 py-2" style={{ borderColor: "#E5E0DA" }}>
            <div className="text-[10px] uppercase tracking-wide text-[#9A958C] font-semibold">{label}</div>
            <div className="text-[15px] font-bold text-[#1F1F1F]">{value}</div>
        </div>
    );
}

function Gantt({ programme, axis, axisLabels }) {
    const n = (axis || []).length;
    if (!n) return <p className="text-sm text-[#6F6A64]">Add stage dates to see the programme.</p>;
    const colW = Math.max(48, Math.min(80, Math.floor(760 / n)));
    return (
        <div className="overflow-x-auto">
            <div style={{ minWidth: 200 + n * colW }}>
                <div className="flex">
                    <div style={{ width: 200 }} className="shrink-0" />
                    {axisLabels.map((l, i) => (
                        <div key={i} style={{ width: colW }} className="text-[10px] text-[#6F6A64] text-center font-medium px-0.5">{l}</div>
                    ))}
                </div>
                {programme.map((p, i) => (
                    <div key={i} className="flex items-center h-9 border-t" style={{ borderColor: "#EDE9E3" }}>
                        <div style={{ width: 200 }} className="shrink-0 text-[12px] text-[#1F1F1F] pr-2 truncate">{p.name}</div>
                        <div className="relative flex" style={{ width: n * colW, height: 24 }}>
                            {p.start_index !== null && p.end_index !== null && (
                                <div className="absolute top-0.5 h-5 rounded-md flex items-center px-2 text-[10px] font-semibold text-white"
                                    style={{
                                        left: p.start_index * colW,
                                        width: (p.end_index - p.start_index + 1) * colW - 4,
                                        background: `linear-gradient(90deg, ${C.brand}, ${C.amber})`,
                                    }}
                                    title={`${p.start_label} → ${p.end_label}`}>
                                    {p.duration_months}mo
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
